import { Spin, Typography } from 'antd'
import { selectPreferredCdf } from 'features/assetDatabase'
import { AssetViz, AssetVizController, AssetVizMode } from 'features/assetViz'
import { EnrichedGeckoInspection } from 'features/inbox/types/types'
import { useRolodexEntries } from 'features/rolodex'
import React, { memo, useEffect, useMemo, useRef } from 'react'
import { GeckoAssetModel, GeckoComponent, RolodexEntryType } from 'types'

const { Text } = Typography

interface InspectionAssetVizProps {
  inspection: EnrichedGeckoInspection
  component?: GeckoComponent
  assetModel?: GeckoAssetModel
  threeDimensionsEnabled?: boolean
}

const InspectionAssetVizComponent: React.FC<InspectionAssetVizProps> = ({
  inspection,
  component,
  assetModel,
  threeDimensionsEnabled = true,
}) => {
  const controllerRef = useRef<AssetVizController>(null)

  // To get CDFs, we query CDSes and let the dispenser convert to CDFs before outputting the response to us.
  const {
    componentDataFrames,
    componentDatasets,
    isLoading: isLoadingCdf,
  } = useRolodexEntries(
    useMemo(() => {
      if (!inspection?.tags?.unit) return undefined

      return {
        type: RolodexEntryType.COMPONENT_DATASET,
        tags: {
          inspection: inspection.id,
        },
        limit: 100,
      }
    }, [inspection.id, inspection?.tags?.unit])
  )

  const cdfToUse = useMemo(() => {
    if (!inspection?.tags?.unit || componentDataFrames.length === 0) {
      return undefined
    }
    return selectPreferredCdf({
      unitId: inspection.tags.unit,
      inspectionId: inspection.id,
      cdfs: componentDataFrames,
      componentDatasets,
    })
  }, [
    inspection?.tags?.unit,
    inspection?.id,
    componentDataFrames,
    componentDatasets,
  ])

  const componentArray = useMemo(() => {
    if (!component) return []
    return [component]
  }, [component])

  const sceneConfig = useMemo(() => {
    return {
      show3D: threeDimensionsEnabled,
      showFeatures: true,
      showFeatureLabels: true,
      showRulers: true,
      showCompass: true,
    }
  }, [threeDimensionsEnabled])

  useEffect(() => {
    if (
      controllerRef.current &&
      assetModel &&
      component &&
      cdfToUse &&
      !isLoadingCdf
    ) {
      const timeoutId = setTimeout(() => {
        const geometry = cdfToUse.data.geometry
        if (geometry) {
          controllerRef.current?.zoomToFit(geometry)
        }
      }, 150)

      return () => clearTimeout(timeoutId)
    }
  }, [assetModel, component, cdfToUse, isLoadingCdf])

  if (isLoadingCdf) {
    return (
      <div
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Spin />
      </div>
    )
  }

  if (!assetModel || !component || !cdfToUse) {
    return (
      <div
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Text type='secondary'>Asset visualisation not available</Text>
      </div>
    )
  }

  return (
    <div
      style={{
        height: '350px',
        width: '100%',
      }}>
      <AssetViz
        ref={controllerRef}
        id={`inspection-${inspection.id}`}
        assetModel={assetModel}
        components={componentArray}
        componentDataFrames={[cdfToUse]}
        selectedInspection={inspection}
        cameraFitMultiplier={0.7}
        sceneConfig={sceneConfig}
        mode={AssetVizMode.SelectBin}
      />
    </div>
  )
}

export const InspectionAssetViz = memo(InspectionAssetVizComponent)
